import type { OrderNode } from '../graphql/queries/orders';
import { metaArrayToRecord } from './orderLineItemMeta';

export type BookingCalendarBucket = 'confirmed' | 'pending' | 'cancelled';

const BOOKING_DATE_META_KEYS = [
  '_ngk_check_in',
  'Booking Date',
  'tat_fields_departure_date',
  'tat_fields_arrival_date',
] as const;

const BUCKET_STYLES: Record<
  BookingCalendarBucket,
  { label: string; className: string; displayOrder: number }
> = {
  cancelled: { label: 'Cancelled', className: 'bg-danger text-white', displayOrder: 0 },
  pending: { label: 'Pending', className: 'bg-warning text-white', displayOrder: 1 },
  confirmed: { label: 'Confirmed', className: 'bg-success text-white', displayOrder: 2 },
};

export interface BookingCalendarSummaryEvent {
  id: string;
  title: string;
  start: string;
  allDay: boolean;
  className: string;
  editable: boolean;
  displayOrder: number;
  extendedProps: {
    bucket: BookingCalendarBucket;
    dateKey: string;
    orderIds: number[];
  };
}

export function mapOrderStatusToBucket(status?: string | null): BookingCalendarBucket {
  const upper = (status || '').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (['CANCELLED', 'REFUNDED', 'FAILED'].includes(upper)) return 'cancelled';
  if (['PROCESSING', 'COMPLETED'].includes(upper)) return 'confirmed';
  return 'pending';
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseToDateKey(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const parsed = new Date(trimmed.includes('T') ? trimmed : `${trimmed}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed);
}

export function getOrderBookingCalendarDate(order: OrderNode): string | null {
  for (const item of order.lineItems?.nodes ?? []) {
    const meta = metaArrayToRecord(item.metaData);
    for (const key of BOOKING_DATE_META_KEYS) {
      const dateKey = parseToDateKey(meta[key]);
      if (dateKey) return dateKey;
    }
  }

  return parseToDateKey(order.date);
}

export function buildBookingCalendarEvents(orders: OrderNode[]): BookingCalendarSummaryEvent[] {
  const groupedOrders = new Map<string, Record<BookingCalendarBucket, number[]>>();

  for (const order of orders) {
    const dateKey = getOrderBookingCalendarDate(order);
    if (!dateKey) continue;

    const bucket = mapOrderStatusToBucket(order.status);
    const dayOrders = groupedOrders.get(dateKey) ?? {
      confirmed: [],
      pending: [],
      cancelled: [],
    };
    dayOrders[bucket].push(order.databaseId);
    groupedOrders.set(dateKey, dayOrders);
  }

  const events: BookingCalendarSummaryEvent[] = [];

  for (const [dateKey, dayOrders] of Array.from(groupedOrders.entries())) {
    (['cancelled', 'pending', 'confirmed'] as BookingCalendarBucket[]).forEach((bucket) => {
      const orderIds = dayOrders[bucket];
      const count = orderIds.length;
      if (count <= 0) return;

      const style = BUCKET_STYLES[bucket];
      events.push({
        id: `${bucket}-${dateKey}`,
        title: `${style.label}: ${count}`,
        start: dateKey,
        allDay: true,
        className: style.className,
        editable: false,
        displayOrder: style.displayOrder,
        extendedProps: {
          bucket,
          dateKey,
          orderIds,
        },
      });
    });
  }

  return events.sort((a, b) => {
    if (a.start !== b.start) return a.start.localeCompare(b.start);
    return a.displayOrder - b.displayOrder;
  });
}
